import { toNumberFormat } from "./Utils";

export const CREDIT_AMOUNT_TYPE_PERCENT = "percent";
export const CREDIT_AMOUNT_TYPE_FIXED = "fixed";

export type CreditAmountDocumentKind = "invoice" | "settlement";

export type CreditAmountTarget = {
    id: string,
    numero: string,
    saldo: number,
    kind: CreditAmountDocumentKind,
};

export type CreditAmountAllocation = CreditAmountTarget & {
    monto: number,
};

const toMoney = (value: number): number => Number(toNumberFormat(value));

/**
 * Monto a favor resultante segun el tipo elegido en el formulario.
 * Para porcentaje la base siempre es el saldo pendiente de los documentos.
 */
export const resolveCreditAmount = (type: string, value: number | string, baseTotal: number): number => {
    const amount = Number(value || 0);
    if (type === CREDIT_AMOUNT_TYPE_PERCENT) {
        return toMoney(baseTotal * amount / 100);
    }
    return toMoney(amount);
};

/**
 * Valida el monto contra el saldo pendiente. Devuelve el mensaje de error o
 * `null` si el monto es valido. `baseTotal` en 0 significa que no hay
 * documentos (saldo a favor suelto del cliente) y solo se exige monto positivo.
 */
export const validateCreditAmount = (amount: number, baseTotal: number): string | null => {
    if (!amount || amount <= 0) {
        return "El monto a favor debe ser mayor a cero";
    }
    if (baseTotal > 0 && amount >= toMoney(baseTotal)) {
        return "El monto a favor no puede ser igual ni superior al saldo pendiente de los documentos seleccionados";
    }
    return null;
};

/**
 * Reparte un monto entre varios documentos de forma proporcional a su saldo.
 * El ultimo documento absorbe la diferencia de redondeo para que la suma de las
 * asignaciones sea exactamente `amount`.
 */
export const distributeByBalance = (amount: number, targets: CreditAmountTarget[]): CreditAmountAllocation[] => {
    if (targets.length === 0) {
        return [];
    }

    const totalAmount = toMoney(amount);
    const baseTotal = targets.reduce((a, t) => a + Number(t.saldo || 0), Number(0));

    if (baseTotal <= 0) {
        throw new Error("No es posible repartir el monto a favor: los documentos seleccionados no tienen saldo pendiente");
    }

    let assigned = 0;
    return targets.map((target, index) => {
        const isLast = index === targets.length - 1;
        const monto = isLast
            ? toMoney(totalAmount - assigned)
            : toMoney(totalAmount * Number(target.saldo || 0) / baseTotal);
        assigned = toMoney(assigned + monto);
        return {
            ...target,
            monto
        };
    });
};
